import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, stationBody } from "../api/scoreApi";
import { getPresenceInstanceId } from "../util/presenceInstanceId";

// Card #3614: publish the bell-to-entry timer from the tablet at judge position C
// so the scoreboard counts it down in sync (POST /ride/:rideId/entry-timer, one
// SNAPSHOT per timer change, never a tick stream).
//
// BEST EFFORT, same doctrine as the Bell's status flip (#768): fire-and-forget,
// deliberately NOT an outbox op, never blocks or delays a press, every failure
// is swallowed and nothing is ever shown for it. Scoring and the tablet's own
// timer never depend on it. The one extra care is the END states (acknowledged,
// cleared): a lost one would leave a countdown on the board, so they are retried
// a few times with a growing delay, unless the server refused them for good.
//
// Also owns the "Over-time prompt" acknowledged flag (GLOSSARY.md), because it
// must survive Pause/Resume and reset only when the timer is cleared.

export const DEFAULT_ENTRY_LIMIT_SECONDS = 45; // the tablet's own fallback
export const END_STATE_RETRY_DELAYS_MS = [1000, 3000, 8000, 20000];

const isPermanentRefusal = (err) => {
  const s = err?.response?.status;
  return Number.isInteger(s) && s >= 400 && s < 500 && s !== 408 && s !== 429;
};

export function useEntryTimerPublish({ rideId, position, test, klass, online }) {
  const scoreApi = useScoreApi();
  const limitSeconds = klass?.faultTimeLimitSeconds ?? DEFAULT_ENTRY_LIMIT_SECONDS;
  // Only the presiding scribe's tablet, only a class that has a timer at all.
  const enabled =
    Boolean(rideId) && position === "C" && test?.hasRideTiming !== false && limitSeconds > 0;
  const [acknowledged, setAcknowledged] = useState(false);

  const live = useRef({});
  live.current = { scoreApi, rideId, limitSeconds, enabled, online };
  // The ride whose timer the server has been told about and not yet told to end.
  const publishedRideRef = useRef(null);
  // Latest seq PER RIDE ID: a retry for ride A must not be dropped because a
  // snapshot was published for ride B in the meantime.
  const latestSeqRef = useRef({});

  const post = useCallback((api, id, body, retriesLeft) => {
    let call;
    try {
      call = api.rides.setRideEntryTimer(id, body);
    } catch {
      call = Promise.reject(new Error("entry timer publish failed"));
    }
    Promise.resolve(call).catch((err) => {
      if (retriesLeft <= 0 || isPermanentRefusal(err)) return;
      const delay = END_STATE_RETRY_DELAYS_MS[END_STATE_RETRY_DELAYS_MS.length - retriesLeft];
      setTimeout(() => {
        // A newer snapshot supersedes this retry.
        if (latestSeqRef.current[id] !== body.seq) return;
        post(api, id, body, retriesLeft - 1);
      }, delay);
    });
  }, []);

  const send = useCallback(
    (state, elapsedMs) => {
      const cur = live.current;
      const isEnd = state === "acknowledged" || state === "cleared";
      if (isEnd) {
        if (publishedRideRef.current !== cur.rideId) return;
      } else if (!cur.enabled || !cur.online) {
        return;
      }
      const seq = Date.now();
      latestSeqRef.current[cur.rideId] = seq;
      const body = stationBody(
        {
          state,
          seq,
          limitSeconds: cur.limitSeconds,
          ...(isEnd ? {} : { elapsedMs: Math.max(0, Math.round(elapsedMs)) }),
        },
        getPresenceInstanceId(),
      );
      if (state === "cleared") {
        publishedRideRef.current = null;
        setAcknowledged(false);
      } else if (!isEnd) {
        publishedRideRef.current = cur.rideId;
      }
      post(cur.scoreApi, cur.rideId, body, isEnd ? END_STATE_RETRY_DELAYS_MS.length : 0);
    },
    [post],
  );

  const publish = useCallback((state, elapsedMs = 0) => send(state, elapsedMs), [send]);
  const acknowledge = useCallback(() => {
    setAcknowledged(true);
    send("acknowledged");
  }, [send]);

  // Leaving the ride (or the screen) with a timer still published ends it, so a
  // countdown never outlives the tablet's own timer (which is local state).
  useEffect(
    () => () => {
      const id = publishedRideRef.current;
      if (!id) return;
      publishedRideRef.current = null;
      const seq = Date.now();
      latestSeqRef.current[id] = seq;
      post(
        live.current.scoreApi,
        id,
        stationBody({ state: "cleared", seq, limitSeconds: live.current.limitSeconds }, getPresenceInstanceId()),
        END_STATE_RETRY_DELAYS_MS.length,
      );
    },
    [rideId, post],
  );
  useEffect(() => setAcknowledged(false), [rideId]);

  return { publish, acknowledge, acknowledged, enabled, limitSeconds };
}
